/**
 * Account + favorites.
 * - Anonymous: favorites live in localStorage (this browser only).
 * - Signed in with Google: favorites live in the account; local ones are merged in at sign-in and
 *   the local copy is kept as a mirror so the heart state is instant on the next visit.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BASE } from "./api";

export type User = { email: string; name: string | null; picture: string | null };

type Ctx = {
  user: User | null;
  ready: boolean;
  googleClientId: string | null;
  favorites: Set<string>;
  isFavorite: (slug: string) => boolean;
  toggleFavorite: (slug: string) => boolean; // returns the new state
  signInWithGoogle: (credential: string, locale: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AccountCtx = createContext<Ctx | null>(null);
const KEY = "artlove365.favorites";

function readLocal(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((s) => typeof s === "string").slice(0, 500) : [];
  } catch { return []; }
}
function writeLocal(slugs: Iterable<string>) {
  try { localStorage.setItem(KEY, JSON.stringify([...slugs].slice(0, 500))); } catch { /* private mode: in-memory only */ }
}

const call = (path: string, init: RequestInit = {}) =>
  fetch(BASE + path, { credentials: "include", ...init, headers: { "Content-Type": "application/json", ...(init.headers ?? {}) } });

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [googleClientId, setClientId] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set(readLocal()));

  // Other tabs: keep hearts in sync.
  useEffect(() => {
    const on = (e: StorageEvent) => { if (e.key === KEY) setFavorites(new Set(readLocal())); };
    window.addEventListener("storage", on);
    return () => window.removeEventListener("storage", on);
  }, []);

  const loadServerFavorites = useCallback(async (merge: boolean) => {
    const res = merge
      ? await call("/v1/account/favorites/merge", { method: "POST", body: JSON.stringify({ slugs: readLocal() }) })
      : await call("/v1/account/favorites");
    if (!res.ok) return;
    const { slugs } = (await res.json()) as { slugs: string[] };
    const set = new Set(slugs);
    setFavorites(set);
    writeLocal(set);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [cfg, me] = await Promise.all([call("/v1/account/config").then((r) => r.json()), call("/v1/account/me").then((r) => r.json())]);
        setClientId(cfg.googleClientId ?? null);
        if (me.user) { setUser(me.user); await loadServerFavorites(true); }
      } catch { /* API unreachable: local favorites still work */ }
      setReady(true);
    })();
  }, [loadServerFavorites]);

  const toggleFavorite = useCallback((slug: string) => {
    const now = !favorites.has(slug);
    const next = new Set(favorites);
    if (now) next.add(slug); else next.delete(slug);
    setFavorites(next);
    writeLocal(next);
    if (user) call(`/v1/account/favorites/${encodeURIComponent(slug)}`, { method: now ? "PUT" : "DELETE" }).catch(() => {});
    return now;
  }, [favorites, user]);

  const signInWithGoogle = useCallback(async (credential: string, locale: string) => {
    const res = await call("/v1/account/auth/google", { method: "POST", body: JSON.stringify({ credential, locale }) });
    if (!res.ok) throw new Error(`sign-in failed (${res.status})`);
    setUser(((await res.json()) as { user: User }).user);
    await loadServerFavorites(true);
  }, [loadServerFavorites]);

  const signOut = useCallback(async () => {
    await call("/v1/account/auth/logout", { method: "POST" }).catch(() => {});
    setUser(null);
  }, []);

  const value = useMemo<Ctx>(() => ({
    user, ready, googleClientId, favorites, isFavorite: (s) => favorites.has(s), toggleFavorite, signInWithGoogle, signOut,
  }), [user, ready, googleClientId, favorites, toggleFavorite, signInWithGoogle, signOut]);
  return <AccountCtx.Provider value={value}>{children}</AccountCtx.Provider>;
}

export function useAccount() {
  const ctx = useContext(AccountCtx);
  if (!ctx) throw new Error("useAccount outside AccountProvider");
  return ctx;
}
